/**
 * Level crossings: where a road crosses a railway on the level, found from
 * their centrelines (a road and a track within a metre of each other in
 * height where they cross; a bridge or an underpass is metres apart), and
 * how a crossing works as a train comes.
 *
 * Several tracks over one road are one crossing: the crossings of one road
 * within a few metres of each other are gathered, and its barriers stand
 * outside all its tracks.
 *
 * A crossing closes as Swedish half barriers do: a train coming (`coming`),
 * the lights flash and the bell rings, and some seconds on the booms come
 * down; it stays shut while any of the train is on it, and a moment after
 * the last car has gone the booms rise and the lights go out (`stepCrossing`).
 *
 * Pure: `three/railCrossings.ts` draws them and hears them.
 */

/** A line, scene x, y, z points; `halfWidth` for a road. */
export interface Centreline {
  points: number[];
  halfWidth?: number;
}

export interface Crossing {
  /** Where the road's centreline meets the tracks' middle. */
  x: number;
  y: number;
  z: number;
  /** The road's direction there, unit. */
  roadX: number;
  roadZ: number;
  /** The road's half width, metres. */
  halfWidth: number;
  /** How far along the road the tracks reach either side of the middle, metres. */
  reach: number;
}

/** A road and a track within this of each other in height where they cross are on the level, metres. */
const LEVEL = 1.0;
/** Crossings of one road this close together are one crossing, metres. */
const GATHER = 14;

/**
 * Every crossing of a road in `roads` with a track in `rails` on the
 * level. Each road segment is tested against each track segment near it.
 */
export function findCrossings(rails: readonly Centreline[], roads: readonly Centreline[]): Crossing[] {
  const hits: Array<Crossing & { road: number }> = [];
  const railSegs: number[] = [];
  for (const r of rails) for (let i = 0; i + 5 < r.points.length; i += 3) railSegs.push(...r.points.slice(i, i + 6));
  const cell = 32;
  const cells = new Map<string, number[]>();
  for (let s = 0; s < railSegs.length; s += 6) {
    const x0 = Math.min(railSegs[s], railSegs[s + 3]), x1 = Math.max(railSegs[s], railSegs[s + 3]);
    const z0 = Math.min(railSegs[s + 2], railSegs[s + 5]), z1 = Math.max(railSegs[s + 2], railSegs[s + 5]);
    for (let cx = Math.floor(x0 / cell); cx <= Math.floor(x1 / cell); cx++) {
      for (let cz = Math.floor(z0 / cell); cz <= Math.floor(z1 / cell); cz++) {
        const k = `${cx},${cz}`;
        const list = cells.get(k);
        if (list === undefined) cells.set(k, [s]); else list.push(s);
      }
    }
  }
  roads.forEach((road, ri) => {
    const p = road.points;
    for (let i = 0; i + 5 < p.length; i += 3) {
      const ax = p[i], ay = p[i + 1], az = p[i + 2], bx = p[i + 3], by = p[i + 4], bz = p[i + 5];
      const seen = new Set<number>();
      for (let cx = Math.floor(Math.min(ax, bx) / cell); cx <= Math.floor(Math.max(ax, bx) / cell); cx++) {
        for (let cz = Math.floor(Math.min(az, bz) / cell); cz <= Math.floor(Math.max(az, bz) / cell); cz++) {
          for (const s of cells.get(`${cx},${cz}`) ?? []) {
            if (seen.has(s)) continue;
            seen.add(s);
            const hit = intersect(ax, az, bx, bz, railSegs[s], railSegs[s + 2], railSegs[s + 3], railSegs[s + 5]);
            if (hit === undefined) continue;
            const roadY = ay + (by - ay) * hit.t, railY = railSegs[s + 1] + (railSegs[s + 4] - railSegs[s + 1]) * hit.u;
            if (Math.abs(roadY - railY) > LEVEL) continue;
            const len = Math.hypot(bx - ax, bz - az) || 1;
            hits.push({
              x: ax + (bx - ax) * hit.t, y: Math.max(roadY, railY), z: az + (bz - az) * hit.t,
              roadX: (bx - ax) / len, roadZ: (bz - az) / len, halfWidth: road.halfWidth ?? 3, reach: 2.5, road: ri,
            });
          }
        }
      }
    }
  });
  // One road over several tracks: one crossing, its middle between the outer tracks.
  const out: Crossing[] = [];
  const used = new Set<number>();
  hits.forEach((h, i) => {
    if (used.has(i)) return;
    const group = hits.filter((o, j) => !used.has(j) && o.road === h.road && Math.hypot(o.x - h.x, o.z - h.z) < GATHER);
    group.forEach(o => used.add(hits.indexOf(o)));
    const along = group.map(o => (o.x - h.x) * h.roadX + (o.z - h.z) * h.roadZ);
    const lo = Math.min(...along), hi = Math.max(...along), mid = (lo + hi) / 2;
    out.push({
      x: h.x + h.roadX * mid, y: Math.max(...group.map(o => o.y)), z: h.z + h.roadZ * mid,
      roadX: h.roadX, roadZ: h.roadZ, halfWidth: h.halfWidth, reach: (hi - lo) / 2 + 2.5,
    });
  });
  return out;
}

function intersect(ax: number, az: number, bx: number, bz: number, cx: number, cz: number, dx: number, dz: number): { t: number; u: number } | undefined {
  const rx = bx - ax, rz = bz - az, sx = dx - cx, sz = dz - cz;
  const den = rx * sz - rz * sx;
  if (Math.abs(den) < 1e-9) return undefined;
  const t = ((cx - ax) * sz - (cz - az) * sx) / den;
  const u = ((cx - ax) * rz - (cz - az) * rx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? { t, u } : undefined;
}

// --- working ----------------------------------------------------------------------

/** Lights and bell before the booms start down, how long they take to come down and to go up, seconds. */
export const WARNING = 5;
export const LOWERING = 6;
export const RAISING = 5;
/** How long after the last car has gone it opens, seconds. */
export const CLEAR_AFTER = 2;
/** A train is coming when it will reach the crossing within this, seconds, or is this close to it, metres. */
export const COMING_SECONDS = 28;
export const COMING_NEAR = 120;

export interface CrossingState {
  /** Seconds since it began to close (NaN when open and still), and since it was last clear of trains. */
  closing: number;
  clear: number;
  /** 0 the booms up .. 1 down. */
  boom: number;
}

export function openCrossing(): CrossingState {
  return { closing: NaN, clear: Infinity, boom: 0 };
}

/** Whether the lights flash and the bell rings. */
export function signalling(s: CrossingState): boolean {
  return !Number.isNaN(s.closing) || s.boom > 0;
}

/** Whether the traffic must stop for it: closing or shut, the booms on their way down or down. */
export function barring(s: CrossingState): boolean {
  return !Number.isNaN(s.closing);
}

/**
 * On by `dt` seconds: `train` whether a train is coming or on it. Closing,
 * it warns, then lowers; clear of trains a moment, it raises and is open.
 */
export function stepCrossing(s: CrossingState, dt: number, train: boolean): void {
  if (train) {
    s.clear = 0;
    if (Number.isNaN(s.closing)) s.closing = 0;
  } else {
    s.clear += dt;
  }
  if (!Number.isNaN(s.closing)) {
    s.closing += dt;
    if (s.closing > WARNING) s.boom = Math.min(1, s.boom + dt / LOWERING);
    // Down, and clear for a moment: open again.
    if (!train && s.clear > CLEAR_AFTER && s.boom >= 1) s.closing = NaN;
  } else {
    s.boom = Math.max(0, s.boom - dt / RAISING);
  }
}

/**
 * Whether a train is coming at a crossing at (x, z): its front at (fx, fz)
 * going (dx, dz) at `speed`, `length` long, its cars' middles `cars`.
 * Coming: the crossing ahead of it, near its track, within its warning
 * time; or any car on it.
 */
export function coming(x: number, z: number, reach: number, fx: number, fz: number, dx: number, dz: number, speed: number,
  cars: ReadonlyArray<{ x: number; z: number; half: number }>): boolean {
  for (const c of cars) if (Math.hypot(c.x - x, c.z - z) < c.half + reach + 4) return true;
  const ahead = (x - fx) * dx + (z - fz) * dz, across = Math.abs((x - fx) * dz - (z - fz) * dx);
  return ahead > 0 && across < 20 && (ahead < COMING_NEAR || ahead < speed * COMING_SECONDS);
}
